import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { useUnmount } from 'react-use';
import VideoJsPlayer from 'video.js/dist/types/player';
import videojs from 'utils/videojsWrapper';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import PlayButtonOverlay from 'components/PlayButtonOverlay';

type PlayerSkins = 'default' | 'minimal' | 'none';

interface VideoPlayerContentProps {
  aspectRatio?: string;
  autoPauseAfterSeconds?: number;
  autoplay?: boolean;
  className?: string;
  enableClickToTogglePlayback?: boolean;
  id: string;
  loop?: boolean;
  muted?: boolean;
  onError?: () => void;
  playsinline?: boolean;
  poster: string;
  skin?: PlayerSkins;
  sources: { src: string; type: string }[];
  testId?: string;
}

const VideoPlayerContent: React.FC<VideoPlayerContentProps> = ({
  aspectRatio = '16:9',
  autoPauseAfterSeconds,
  autoplay = false,
  className,
  enableClickToTogglePlayback = false,
  id,
  loop = false,
  muted = false,
  onError,
  playsinline = true,
  poster,
  skin,
  sources,
  testId = 'video-player',
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const autoPauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPaused, setIsPaused] = useState(!autoplay);

  const clearAutoPauseTimeout = useCallback(() => {
    if (autoPauseTimeoutRef.current) {
      clearTimeout(autoPauseTimeoutRef.current);
      autoPauseTimeoutRef.current = null;
    }
  }, []);

  const scheduleAutoPause = useCallback(
    (player: VideoJsPlayer) => {
      clearAutoPauseTimeout();

      if (!autoPauseAfterSeconds) return;

      autoPauseTimeoutRef.current = setTimeout(() => {
        if (!player.isDisposed() && !player.paused()) {
          player.pause();
          setIsPaused(true);
        }
      }, autoPauseAfterSeconds * 1000);
    },
    [autoPauseAfterSeconds, clearAutoPauseTimeout],
  );

  const togglePlayback = useCallback(() => {
    if (!enableClickToTogglePlayback) return;

    const player = playerRef.current;
    if (!player || player.isDisposed()) return;

    if (player.paused()) {
      setIsPaused(false);
      const playPromise = player.play();
      if (playPromise) {
        playPromise.catch(() => setIsPaused(true));
      }
    } else {
      player.pause();
      setIsPaused(true);
    }
  }, [enableClickToTogglePlayback]);

  const pauseOnVideoClick = useCallback(() => {
    if (!isPaused) togglePlayback();
  }, [isPaused, togglePlayback]);

  useEffect(() => {
    if (videoRef.current) {
      let player;
      if (!playerRef.current) {
        const videoElement = window.document.createElement('video-js');
        videoElement.setAttribute('id', id);
        videoRef.current.appendChild(videoElement);

        player = videojs(videoElement, {
          aspectRatio,
          autoplay,
          controls: skin !== 'none',
          disablePictureInPicture: true,
          loop,
          muted,
          playsinline,
          poster,
          preload: 'auto',
          sources,
        });
        player.addClass('vjs-big-play-centered vjs-sl-cam-player');
        playerRef.current = player;
      } else {
        player = playerRef.current;
        player.removeClass(
          'vjs-sl-cam-player-minimal vjs-sl-cam-player-none vjs-sl-cam-player-default',
        );
        if (skin === 'none') {
          player.controls(false);
        } else {
          player.controls(true);
        }
        player.autoplay(autoplay);
        player.loop(loop);
        player.muted(muted);
        player.poster(poster);
        player.src(sources);
      }
      // set appropriate skin class
      switch (skin) {
        case 'minimal':
          player.addClass('vjs-sl-cam-player-minimal');
          break;
        case 'none':
          player.addClass('vjs-sl-cam-player-none');
          break;
        default:
          player.addClass('vjs-sl-cam-player-default');
          break;
      }

      const handlePlay = () => {
        setIsPaused(false);
        scheduleAutoPause(player);
      };
      const handlePause = () => {
        setIsPaused(true);
        clearAutoPauseTimeout();
      };
      const shouldManagePlaybackState = enableClickToTogglePlayback || !!autoPauseAfterSeconds;
      if (shouldManagePlaybackState) {
        setIsPaused(!autoplay);
        player.on('play', handlePlay);
        player.on('pause', handlePause);
        if (autoplay) {
          scheduleAutoPause(player);
        }
      }

      const handleError = () => onError?.();
      if (onError) {
        player.one('error', handleError);
      }

      return () => {
        if (shouldManagePlaybackState) {
          player.off('play', handlePlay);
          player.off('pause', handlePause);
          clearAutoPauseTimeout();
        }
        if (onError) {
          player.off('error', handleError);
        }
      };
    }

    return undefined;
  }, [
    aspectRatio,
    autoPauseAfterSeconds,
    autoplay,
    clearAutoPauseTimeout,
    enableClickToTogglePlayback,
    id,
    loop,
    muted,
    onError,
    playsinline,
    poster,
    scheduleAutoPause,
    skin,
    sources,
  ]);

  useUnmount(() => {
    clearAutoPauseTimeout();
    const player = playerRef.current;
    if (player && !player.isDisposed()) {
      try {
        player.dispose();
        playerRef.current = null;
      } catch (error) {
        console?.error(error);
      }
    }
  });

  return (
    <div
      className={className}
      data-testid={testId}
      onClick={enableClickToTogglePlayback ? pauseOnVideoClick : undefined}
      ref={videoRef}
      role={enableClickToTogglePlayback ? 'presentation' : undefined}
    >
      <PlayButtonOverlay
        visible={enableClickToTogglePlayback && isPaused}
        onClick={togglePlayback}
      />
    </div>
  );
};

export interface VideoPlayerProps extends VideoPlayerContentProps {
  error?: { status: number; name: string; message: string };
}

export const VideoPlayer: React.FC<VideoPlayerProps> = (props) => {
  const { error, testId } = props;
  const dataTestId = useMemo(() => (testId ? `${testId}-error` : 'video-player-error'), [testId]);

  return (
    <ErrorBoundary
      error={error}
      errorBoundaryId="video-player"
      render={() => <ErrorMessage dataTestId={dataTestId} />}
    >
      <VideoPlayerContent {...props} />
    </ErrorBoundary>
  );
};

export default VideoPlayer;
